<template>
    <div class="card p-shadow-app">
        <h1>Dossiês Internos</h1>
        <Toast />

        <div class="p-mb-3 p-d-flex p-jc-between p-ai-center">
            <Button
                class="p-mb-3"
                v-if="can('seeAddEditInternalDossiers')"
                label="Novo Dossiê"
                icon="pi pi-plus"
                @click="$router.push({ name: 'InternalDossiersFormView' })"
            />
        </div>

        <div class="p-fluid p-formgrid p-grid p-mb-3">
            <div class="p-field p-col-12 p-md-3">
                <label for="tipodoc">Tipo de Dossiê</label>
                <Dropdown
                    id="tipodoc"
                    v-model="tipodoc"
                    :options="dossierTypes"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Selecione o tipo"
                    :loading="loadingTypes"
                    showClear
                />
            </div>

            <div class="p-field p-col-12 p-md-2">
                <label for="obrano">Número de Dossiê</label>
                <InputText
                    id="obrano"
                    v-model="obrano"
                    placeholder="Filtrar por número de dossiê" />
            </div>

            <div class="p-field p-col-12 p-md-2">
                <label for="dateFrom">Data De</label>
                <Calendar
                    id="dateFrom"
                    v-model="filters.dateFrom"
                    dateFormat="dd/mm/yy"
                    placeholder="Data inicial"
                />
            </div>

            <div class="p-field p-col-12 p-md-2">
                <label for="dateTo">Data Até</label>
                <Calendar
                    id="dateTo"
                    v-model="filters.dateTo"
                    dateFormat="dd/mm/yy"
                    placeholder="Data final"
                />
            </div>

            <div class="p-field p-col-12 p-md-2 p-d-flex p-ai-end">
                <Button
                    label="Aplicar Filtros"
                    icon="pi pi-filter"
                    @click="fetchWithFilters"
                />
            </div>

            <div class="p-field p-col-12 p-md-1 p-d-flex p-ai-end">
                <Button
                    label="Limpar"
                    icon="pi pi-times"
                    class="p-button-secondary p-button-text"
                    @click="clearStoredFilters"
                />
            </div>
        </div>

        <DataTable
            :value="dossierList"
            :loading="loading"
            :paginator="true"
            :rows="perPage"
            :rowsPerPageOptions="[10, 20, 50, 100]"
            responsiveLayout="scroll"
            class="p-datatable-sm"
            stripedRows
            :filters.sync="tableFilters"
            filterDisplay="menu"
            :lazy="true"
            :totalRecords="totalRecords"
            @filter="fetchDossiers"
            @sort="fetchDossiers"
            @page="fetchDossiers"
            :expandedRows.sync="expandedRows"
            @row-expand="onRowExpand"
            @row-collapse="onRowCollapse"
        >
            <template #empty>
                <div class="p-text-center p-p-3">
                    <i class="pi pi-inbox" style="font-size: 3rem; color: #999;"></i>
                    <p class="p-mt-2">Nenhum dossiê encontrado</p>
                </div>
            </template>

            <Column :expander="true" />

            <Column field="id" header="Referência" :sortable="true" style="min-width: 150px">
                <template #body="slotProps">
                    <strong>{{ slotProps.data.id }}</strong>
                </template>
            </Column>

            <Column field="type_name" header="Tipo" :sortable="true" style="min-width: 180px">
                <template #body="slotProps">
                    <Tag :value="slotProps.data.type_name" severity="info" />
                </template>
            </Column>

            <Column field="name" header="Nome/Cliente/Fornecedor" :sortable="true" style="min-width: 200px">
                <template #body="slotProps">
                    {{ slotProps.data.name || '-' }}
                </template>
            </Column>

            <Column field="date" header="Data" :sortable="true" style="min-width: 120px">
                <template #body="slotProps">
                    {{ formatDate(slotProps.data.date) }}
                </template>
            </Column>

            <Column field="observation" header="Observações" style="min-width: 200px">
                <template #body="slotProps">
                    <span class="p-text-truncate" style="max-width: 200px; display: block;">
                        {{ slotProps.data.observation || '-' }}
                    </span>
                </template>
            </Column>

            <Column field="closed" header="Estado" :sortable="true" style="min-width: 100px">
                <template #body="slotProps">
                    <Tag
                        :value="slotProps.data.closed ? 'Fechado' : 'Aberto'"
                        :severity="slotProps.data.closed ? 'success' : 'warning'"
                    />
                </template>
            </Column>

            <Column field="subtotal" header="Total" :sortable="true" style="min-width: 120px">
                <template #body="slotProps">
                    {{ formatCurrency(slotProps.data.subtotal) }}
                </template>
            </Column>

            <Column field="total" header="Total (c/iva)" :sortable="true" style="min-width: 120px">
                <template #body="slotProps">
                    {{ formatCurrency(slotProps.data.total) }}
                </template>
            </Column>

            <Column header="Ações" style="min-width: 150px">
                <template #body="slotProps">
                    <div class="p-d-flex p-gap-2">
                        <Button
                            icon="pi pi-pencil"
                            class="p-button-rounded p-button-success p-button-text"
                            v-tooltip.top="'Editar'"
                            @click="editDossier(slotProps.data)"
                        />
                        <Button
                            icon="pi pi-copy"
                            class="p-button-rounded p-button-primary p-button-text"
                            v-tooltip.top="'Converter para outro tipo'"
                            @click="openConvertDialog(slotProps.data)"
                        />
                        <Button
                            v-if="slotProps.data.type_id === 19 && can('seeShipmentTracking')"
                            icon="pi pi-send"
                            class="p-button-rounded p-button-help p-button-text"
                            v-tooltip.top="'Expedir peças'"
                            :loading="shipmentLoadingDossierId === slotProps.data.reference"
                            @click="openShipmentDialog(slotProps.data)"
                        />
                        <PreviewInternalDossierReport
                            v-if="slotProps.data.type_id === 3"
                            :report="'commercial_offer'"
                            :stamp="slotProps.data.reference"
                        />

                        <PreviewInternalDossierReport
                            v-if="slotProps.data.type_id === 19"
                            :report="'spares_orders'"
                            :stamp="slotProps.data.reference"
                        />
                        <!-- <Button
                            icon="pi pi-trash"
                            class="p-button-rounded p-button-danger p-button-text"
                            v-tooltip.top="'Eliminar'"
                            @click="confirmDelete(slotProps.data)"
                            :disabled="slotProps.data.closed"
                        /> -->
                    </div>
                </template>
            </Column>

            <template #expansion="slotProps">
                <div class="p-4" v-if="slotProps.data.lines && slotProps.data.lines.length">
                    <DataTable :value="slotProps.data.lines" responsiveLayout="scroll" class="p-datatable-sm">
                        <Column v-for="(code, index) in Object.keys(expandedViewsFields)"
                            :key="index + '-' + code"
                            :field="code"
                            :header="expandedViewsFields[code].label">
                            <template #body="lineSlotProps">
                                <span v-if="expandedViewsFields[code].type === 'currency'">
                                    {{ formatCurrency(lineSlotProps.data[code]) }}
                                </span>
                                <span v-else-if="expandedViewsFields[code].type === 'percentage'">
                                    {{ formatDiscount(lineSlotProps.data[code]) }}
                                </span>
                                <span v-else>
                                    {{ lineSlotProps.data[code] ?? '-' }}
                                </span>
                            </template>
                        </Column>
                    </DataTable>
                </div>
                <div v-else>
                    <div class="p-text-center p-p-3">
                        <i class="pi pi-inbox" style="font-size: 2rem; color: #999;"></i>
                        <p class="p-mt-2">Sem linhas para exibir</p>
                    </div>
                </div>
            </template>
        </DataTable>

        <!-- Delete Confirmation Dialog -->
        <Dialog
            :visible.sync="deleteDialog"
            :style="{ width: '450px' }"
            header="Confirmar Eliminação"
            :modal="true"
        >
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem; color: var(--red-500);" />
                <span>
                    Tem certeza que deseja eliminar o dossiê <strong>{{ selectedDossier?.reference }}</strong>?
                    <br />Esta ação não pode ser desfeita.
                </span>
            </div>
            <template #footer>
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="deleteDialog = false"
                />
                <Button
                    label="Eliminar"
                    icon="pi pi-check"
                    class="p-button-danger"
                    @click="deleteDossier"
                    :loading="deleting"
                />
            </template>
        </Dialog>

        <!-- View Details Dialog -->
        <Dialog
            :visible.sync="detailsDialog"
            :style="{ width: '800px' }"
            :header="`Dossiê - ${selectedDossier?.id}`"
            :modal="true"
            :maximizable="true"
        >
            <div v-if="selectedDossier" class="p-fluid">
                <div class="p-grid">
                    <div class="p-col-12 p-md-12">
                        <h4>Informações Gerais</h4>
                        <dl>
                            <dt>Tipo:</dt>
                            <dd><Tag :value="selectedDossier.type_name" severity="info" /></dd>

                            <dt>Referência:</dt>
                            <dd>
                                <strong>{{ selectedDossier.id }}</strong><br />
                                <small>{{ selectedDossier.reference }}</small>
                            </dd>

                            <dt>Data:</dt>
                            <dd>{{ formatDate(selectedDossier.date) }}</dd>

                            <dt>Nome:</dt>
                            <dd>{{ selectedDossier.name || '-' }}</dd>

                            <dt>Estado:</dt>
                            <dd>
                                <Tag
                                    :value="selectedDossier.closed ? 'Fechado' : 'Aberto'"
                                    :severity="selectedDossier.closed ? 'success' : 'warning'"
                                />
                            </dd>
                        </dl>
                    </div>

                    <div class="p-col-12 p-md-12" v-if="selectedDossier.lines">
                        <h4>Linhas</h4>
                        <DataTable
                            :value="selectedDossier.lines"
                            class="p-datatable-sm"
                            responsiveLayout="scroll"
                            stripedRows
                        >
                            <Column field="reference" header="Descrição">
                                <template #body="slotProps">
                                    {{ slotProps.data.reference || '-' }} - {{ slotProps.data.design || '-' }}
                                </template>
                            </Column>
                            <Column field="quantity" header="Quantidade" style="width: 120px;">
                                <template #body="slotProps">
                                    {{ slotProps.data.quantity || '-' }}
                                </template>
                            </Column>
                            <Column field="costPrice" header="Preço Unitário" style="width: 150px;">
                                <template #body="slotProps">
                                    {{ slotProps.data.costPrice || '-' }}
                                </template>
                            </Column>
                            <Column field="warehouse" header="Armazém" style="width: 150px;">
                                <template #body="slotProps">
                                    {{ slotProps.data.warehouse || '-' }} {{ slotProps.data.warehouse_name ? `- ${slotProps.data.warehouse_name}` : '' }}
                                </template>
                            </Column>
                        </DataTable>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button
                    label="Fechar"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="detailsDialog = false"
                />
            </template>
        </Dialog>

        <Dialog
            :visible.sync="shipmentDialog"
            :style="{ width: '95vw', maxWidth: '1200px' }"
            :header="shipmentDialogHeader"
            :modal="true"
            :maximizable="shipmentCarrier === 'NACEX'"
            class="shipment-dialog"
        >
            <div v-if="!shipmentCarrier" class="p-d-flex p-flex-column p-ai-center p-p-4" style="gap: 1.5rem;">
                <p style="font-size: 1.1rem; margin-bottom: 0.5rem;">Selecione a transportadora:</p>
                <div class="p-d-flex" style="gap: 1rem;">
                    <Button
                        label="NACEX"
                        icon="pi pi-send"
                        class="p-button-lg p-button-info"
                        style="min-width: 180px;"
                        @click="shipmentCarrier = 'NACEX'"
                    />
                    <!-- <Button
                        label="Outra"
                        icon="pi pi-truck"
                        class="p-button-lg p-button-secondary"
                        style="min-width: 180px;"
                        @click="shipmentCarrier = 'OTHER'"
                    /> -->
                </div>
            </div>

            <NacexShippingForm
                v-if="shipmentDialog && shipmentDossier && shipmentCarrier === 'NACEX'"
                :mode="'internal_dossier'"
                :internalDossierId="shipmentDossier.reference"
                :defaultData="shipmentDefaultData"
                :embedded="true"
                @success="onShipmentSuccess"
                @cancel="closeShipmentDialog"
            />

            <InternalDossierManualShipment
                v-if="shipmentDialog && shipmentDossier && shipmentCarrier === 'OTHER'"
                :internalDossierId="shipmentDossier.reference"
                :defaultData="shipmentDefaultData"
                @success="onShipmentSuccess"
                @cancel="closeShipmentDialog"
            />
        </Dialog>

        <Dialog
            :visible.sync="convertDialog"
            :style="{ width: '520px' }"
            header="Converter Dossiê"
            :modal="true"
        >
            <div class="p-fluid">
                <div class="p-field">
                    <label for="convertTargetType">Tipo de destino</label>
                    <Dropdown
                        id="convertTargetType"
                        v-model="convertTargetType"
                        :options="availableConversionTypes"
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Selecione o tipo destino"
                        appendTo="body"
                    />
                </div>
            </div>
            <template #footer>
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="closeConvertDialog"
                />
                <Button
                    label="Abrir formulário"
                    icon="pi pi-check"
                    :disabled="!convertTargetType"
                    :loading="convertLoading"
                    @click="startConversion"
                />
            </template>
        </Dialog>
    </div>
</template>

<script>
import dossierService from '@/services/internalDossier.service';
import { FilterMatchMode } from 'primevue/api';
import PreviewInternalDossierReport from '@/components/internalDossier/reports/PreviewInternalDossierReport.vue';
import NacexShippingForm from '@/components/shipment/NacexShippingForm.vue';
import InternalDossierManualShipment from '@/components/internalDossier/InternalDossierManualShipment.vue';
import Tag from 'primevue/tag';

export default {
    name: "InternalDossiers",
    storageKey: 'internal_dossiers_filters',
    conversionStorageKey: 'internal_dossier_conversion_draft',
    components: {
        Tag,
        PreviewInternalDossierReport,
        NacexShippingForm,
        InternalDossierManualShipment
    },
    watch: {
        tipodoc() {
            this.persistFilters();
        },
        obrano() {
            this.persistFilters();
        },
        'filters.dateFrom'() {
            this.persistFilters();
        },
        'filters.dateTo'() {
            this.persistFilters();
        }
    },

    data() {
        return {
            tipodoc: null,
            obrano: '',
            dossierTypes: [],
            dossierList: [],
            loadingTypes: false,
            loading: false,
            deleting: false,

            filters: {
                dateFrom: null,
                dateTo: null
            },

            totalRecords: 0,
            currentPage: 1,
            perPage: 20,

            tableFilters: {
                global: { value: null, matchMode: FilterMatchMode.CONTAINS }
            },

            deleteDialog: false,
            detailsDialog: false,
            shipmentDialog: false,
            shipmentCarrier: null,
            convertDialog: false,
            convertLoading: false,
            selectedDossier: null,
            convertSourceDossier: null,
            convertTargetType: null,
            shipmentDossier: null,
            shipmentLoadingDossierId: null,
            shipmentDefaultData: {
                reference: '',
                client: null,
                entity: null
            },
            expandedRows: [],
            expandedViewsFields: {}
        };
    },

    activated() {
        this.restoreFilters();
        this.fetchDossiers();
    },

    computed: {
        selectedTypeName() {
            if (!this.tipodoc) return null;
            const type = this.dossierTypes.find(t => t.id === this.tipodoc);
            return type?.name;
        },
        availableConversionTypes() {
            if (!this.convertSourceDossier) {
                return [];
            }

            return this.dossierTypes.filter(type => type.id !== this.convertSourceDossier.type_id);
        },
        shipmentDialogHeader() {
            if (!this.shipmentCarrier) return 'Expedição de Peças';
            if (this.shipmentCarrier === 'NACEX') return 'Expedição NACEX';
            return 'Expedição Manual';
        }
    },

    created() {
        this.restoreFilters();
        this.loadDossierTypes();
        this.fetchDossiers();
    },

    methods: {
        persistFilters() {
            const payload = {
                tipodoc: this.tipodoc,
                obrano: this.obrano,
                dateFrom: this.filters.dateFrom ? this.formatDateForStorage(this.filters.dateFrom) : null,
                dateTo: this.filters.dateTo ? this.formatDateForStorage(this.filters.dateTo) : null
            };

            localStorage.setItem(this.$options.storageKey, JSON.stringify(payload));
        },
        restoreFilters() {
            const rawValue = localStorage.getItem(this.$options.storageKey);
            if (!rawValue) {
                return;
            }

            try {
                const savedFilters = JSON.parse(rawValue);
                this.tipodoc = savedFilters.tipodoc ?? null;
                this.obrano = savedFilters.obrano ?? '';
                this.filters.dateFrom = savedFilters.dateFrom ? new Date(savedFilters.dateFrom) : null;
                this.filters.dateTo = savedFilters.dateTo ? new Date(savedFilters.dateTo) : null;
            } catch (error) {
                localStorage.removeItem(this.$options.storageKey);
            }
        },
        clearStoredFilters() {
            this.tipodoc = null;
            this.obrano = '';
            this.filters.dateFrom = null;
            this.filters.dateTo = null;
            localStorage.removeItem(this.$options.storageKey);
            this.fetchDossiers();
        },
        onRowExpand(event) {
            this.expandedViewsFields = this.getViewExpandableRowsLines(event.data.type_id);
            this.expandedRows = event?.data ? [event.data] : [];
        },
        onRowCollapse() {
            this.expandedRows = [];
            this.expandedViewsFields = {};
        },
        async loadDossierTypes() {
            try {
                this.loadingTypes = true;
                this.dossierTypes = await dossierService.getConfigTypes();
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao carregar tipos de dossier',
                    life: 3000
                });
            } finally {
                this.loadingTypes = false;
            }
        },

        getViewExpandableRowsLines(type) {
            let config = this.dossierTypes.find(t => t.id === type)?.view_fields || false;
            return config.lines || {};
        },

        async fetchDossiers(event = {}) {
            try {
                this.loading = true;

                const page = event.page ? event.page + 1 : 1;

                const params = {
                    page,
                    per_page: this.perPage
                };

                if (this.tipodoc) {
                    params.ndos = this.tipodoc;
                }

                if (this.obrano) {
                    params.obrano = this.obrano;
                }

                if (this.filters.dateFrom) {
                    params.date_from = this.formatDateForAPI(this.filters.dateFrom);
                }

                if (this.filters.dateTo) {
                    params.date_to = this.formatDateForAPI(this.filters.dateTo);
                }

                const response = await dossierService.getDossiers(params);

                this.dossierList = response.data;
                this.totalRecords = response.meta.total;
                this.currentPage = response.meta.current_page;

            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao carregar dossiês',
                    life: 3000
                });
            } finally {
                this.loading = false;
            }
        },

        fetchWithFilters() {
            this.persistFilters();
            this.fetchDossiers();
        },

        viewDossier(dossier) {
            this.selectedDossier = dossier;
            this.detailsDialog = true;
        },

        editDossier(dossier) {
            this.$router.push({
                name: 'InternalDossiersFormUpdateView',
                params: {
                    reference: dossier.reference
                }
            });
        },

        openConvertDialog(dossier) {
            this.convertSourceDossier = dossier;
            this.convertTargetType = null;
            this.convertDialog = true;
        },

        closeConvertDialog() {
            this.convertDialog = false;
            this.convertLoading = false;
            this.convertSourceDossier = null;
            this.convertTargetType = null;
        },

        async startConversion() {
            if (!this.convertSourceDossier || !this.convertTargetType) {
                return;
            }

            try {
                this.convertLoading = true;
                const response = await dossierService.getConversionDraft(
                    this.convertSourceDossier.type_id,
                    this.convertTargetType,
                    this.convertSourceDossier.reference,
                );

                sessionStorage.setItem(this.$options.conversionStorageKey, JSON.stringify(response));
                this.closeConvertDialog();
                this.$router.push({
                    name: 'InternalDossiersFormView',
                    query: { draft: '1' }
                });
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Não foi possível preparar a conversão do dossier',
                    life: 3000
                });
                this.convertLoading = false;
            }
        },

        normalizeCountryCode(country) {
            if (!country) {
                return 'PT';
            }

            const normalized = `${country}`.trim().toUpperCase();
            if (normalized.length === 2) {
                return normalized;
            }

            if (normalized === 'PORTUGAL') {
                return 'PT';
            }

            return 'PT';
        },

        buildShipmentClientData(client) {
            if (!client) {
                return null;
            }

            let rawZip = client.zipCode || '';
            let finalZip = rawZip;
            let finalCity = client.city || '';

            if (rawZip.trim().includes(' ')) {
                const parts = rawZip.trim().split(' ');
                finalZip = parts[0];

                if (!finalCity && parts.length > 1) {
                    finalCity = parts.slice(1).join(' ');
                }
            }

            return {
                name: client.name || '',
                address: client.address || '',
                zip: finalZip,
                city: finalCity,
                country: this.normalizeCountryCode(client.country),
                phone: client.mobilPhone || client.telephone || '',
                email: client.email || ''
            };
        },

        async openShipmentDialog(dossier) {
            try {
                this.shipmentLoadingDossierId = dossier.reference;

                const client = await dossierService.getClientInfoForDossier(dossier.reference);

                if (!client || client.error) {
                    throw new Error('Cliente não encontrado para este dossiê');
                }
                let reference = `${dossier.id}`;

                this.shipmentDossier = dossier;
                this.shipmentDefaultData = {
                    reference: reference,
                    client: this.buildShipmentClientData(client),
                    entity: null
                };
                this.shipmentDialog = true;
                this.shipmentCarrier = null;
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: error.message || 'Não foi possível preparar a expedição',
                    life: 3000
                });
            } finally {
                this.shipmentLoadingDossierId = null;
            }
        },

        onShipmentSuccess() {
            this.$toast.add({
                severity: 'success',
                summary: 'Sucesso',
                detail: 'Expedição criada com sucesso',
                life: 3000
            });
        },

        closeShipmentDialog() {
            this.shipmentDialog = false;
            this.shipmentCarrier = null;
            this.shipmentDossier = null;
            this.shipmentDefaultData = {
                reference: '',
                client: null,
                entity: null
            };
        },

        confirmDelete(dossier) {
            this.selectedDossier = dossier;
            this.deleteDialog = true;
        },

        async deleteDossier() {
            try {
                this.deleting = true;

                await dossierService.deleteDossier(this.selectedDossier.id);

                this.$toast.add({
                    severity: 'success',
                    summary: 'Sucesso',
                    detail: 'Dossiê eliminado com sucesso',
                    life: 3000
                });

                this.deleteDialog = false;
                this.selectedDossier = null;

                await this.fetchDossiers();
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Erro',
                    detail: 'Erro ao eliminar dossiê',
                    life: 3000
                });
            } finally {
                this.deleting = false;
            }
        },

        formatDate(dateString) {
            if (!dateString) return '-';

            const date = new Date(dateString);
            return date.toLocaleDateString('pt-PT', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        },

        formatDateForAPI(date) {
            if (!date) return null;

            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        },
        formatDateForStorage(date) {
            if (!date) return null;

            const d = new Date(date);
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('pt-PT', {
                style: 'currency',
                currency: 'EUR'
            }).format(value || 0);
        },
        formatDiscount(value) {
            return `${value || 0}%`;
        }
    }
};
</script>

<style scoped>
.card {
    padding: 2rem;
}

h1 {
    margin-bottom: 1.5rem;
    color: #495057;
}

.p-field label {
    display: block;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

dl {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 0.5rem;
    margin: 0;
}

dt {
    font-weight: 600;
    color: #6c757d;
}

dd {
    margin: 0;
}

.confirmation-content {
    display: flex;
    align-items: center;
    padding: 1rem 0;
}

pre {
    background: #f8f9fa;
    padding: 1rem;
    border-radius: 4px;
    overflow-x: auto;
}

.p-gap-2 {
    gap: 0.5rem;
}
</style>
